import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  Span,
  SpanProcessor,
} from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";

const SERVICE_NAME = "uam-ui";
const SERVICE_VERSION = "1.0.0";

interface UserContext {
  userId?: string;
  companyId?: string;
  email?: string;
}

let currentUser: UserContext = {};

/**
 * Stamps the active user's identity onto every span as it starts. Spans
 * created before the user resolves simply carry no identity; spans created
 * afterwards (the vast majority — API calls, clicks, navigations) are tagged.
 */
class UserContextSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    if (currentUser.userId) {
      span.setAttribute("UserId", currentUser.userId);
    }
    if (currentUser.companyId) {
      span.setAttribute("CompanyId", currentUser.companyId);
    }
    if (currentUser.email) {
      span.setAttribute("Email", currentUser.email);
    }
  }

  onEnd(): void {
    // no-op
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const toTracesEndpoint = (url: string): string => {
  const trimmed = url.replace(/\/+$/, "");

  return trimmed.endsWith("/v1/traces") ? trimmed : `${trimmed}/v1/traces`;
};

export const setUserContext = (user: UserContext): void => {
  currentUser = user;
};

let initialized = false;

/**
 * Initialises browser OpenTelemetry tracing and ships spans to the external
 * OTLP/HTTP exporter URL provided by AppConfig (`Otlp:AlloyApiUrl`), authorised
 * with the subscription key (`Otlp:AlloyApiSubscriptionKey`). No-ops when the
 * exporter URL is unset, so local dev and tests stay clean unless configured.
 */
export const initTelemetry = (
  exporterUrl?: string,
  subscriptionKey?: string,
): void => {
  if (initialized) return;
  // Only initialise when both the exporter URL and subscription key are
  // present; a missing/empty value means telemetry is not configured.
  if (!exporterUrl || !subscriptionKey) return;

  const env = process.env as Record<string, string | undefined>;
  const deploymentEnvironment = env.REACT_APP_ENVIRONMENT ?? "unknown";
  const backendAddress = env.backendAddress;

  initialized = true;

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    [ATTR_SERVICE_VERSION]: SERVICE_VERSION,
    "deployment.environment": deploymentEnvironment,
  });

  const exporter = new OTLPTraceExporter({
    url: toTracesEndpoint(exporterUrl),
    headers: { "Ocp-Apim-Subscription-Key": subscriptionKey },
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new UserContextSpanProcessor(),
      new BatchSpanProcessor(exporter),
    ],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  // Propagate W3C trace context to backend calls so frontend spans can be
  // correlated with backend traces. Requires the backend CORS policy to
  // allow the `traceparent` request header.
  const propagateTraceHeaderCorsUrls = backendAddress
    ? [new RegExp(escapeRegExp(backendAddress))]
    : undefined;

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        clearTimingResources: true,
        propagateTraceHeaderCorsUrls,
      }),
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation(),
    ],
  });
};
